import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './DestinationsMap.css';
import { images } from '../../shared/assets';
import { HandDrawnButton } from '../../shared/HandDrawnButton/HandDrawnButton';

// Clean Switch component
interface SwitchProps {
  label: string;
  isActive?: boolean;
  onClick?: () => void;
}

const Switch: React.FC<SwitchProps> = ({ label, isActive = false, onClick }) => {
  return (
    <div
      className={`destinations-map__switch ${isActive ? 'destinations-map__switch--active' : ''}`}
      onClick={onClick}
    >
      <div className="destinations-map__switch-toggle">
        <div className="destinations-map__switch-track">
          <div className="destinations-map__switch-thumb"></div>
        </div>
      </div>
      <div className="destinations-map__switch-label">
        <p>{label.toUpperCase()}</p>
      </div>
    </div>
  );
};

interface DestinationsMapProps {
  showDestinationsText?: boolean;
}

export const DestinationsMap: React.FC<DestinationsMapProps> = ({ showDestinationsText = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showAirports, setShowAirports] = useState(false);
  const [showFlights, setShowFlights] = useState(false);
  const [isHovered, setIsHovered] = useState<string | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  
  // Add staggered animation delays for airports
  useEffect(() => {
    const airports = mapRef.current?.querySelectorAll('.destinations-map__airport');
    airports?.forEach((airport, index) => {
      (airport as HTMLElement).style.transitionDelay = `${index * 100}ms`;
    });
  }, []);

  return (
    <section className="destinations-map">
      <div className="destinations-map__container">
        {/* Text Block + Button */}
        <div className="destinations-map__text-block">
          <div className="destinations-map__title">
            <p>{t('pages/home:map.title')}</p>
          </div>
          <div className="destinations-map__body">
            <div className="destinations-map__description">
              <p>{t('pages/home:map.description')}</p>
            </div>
            <HandDrawnButton
              text={t('pages/home:map.cta')}
              onClick={() => navigate('/destinations')}
              variant="brand"
              color="awasi"
              animation="stroke-to-fill"
              size="normal"
              className="destinations-map__button"
            />
          </div>
        </div>

        {/* Map */}
        <div className="destinations-map__map" ref={mapRef}>
          {/* Map Toggles */}
          <div className="destinations-map__toggles">
            <Switch 
              label={t('pages/home:map.toggles.airports')} 
              isActive={showAirports}
              onClick={() => setShowAirports(!showAirports)}
            />
            <Switch 
              label={t('pages/home:map.toggles.flights')} 
              isActive={showFlights}
              onClick={() => setShowFlights(!showFlights)}
            />
          </div>

          {/* Map Vector */}
          <div className="destinations-map__vector">
            <img src={images.mapIcons.southAmericaMap} alt={t('pages/home:map.altTexts.southAmericaMap')} />
          </div>

          {/* Destinations */}
          <div className="destinations-map__destinations">
            {/* Destination Labels - Exact from Figma */}
            <div 
              className={`destinations-map__destination destinations-map__destination--atacama ${isHovered === 'atacama' ? 'destinations-map__destination--hover' : ''}`}
              onMouseEnter={() => setIsHovered('atacama')}
              onMouseLeave={() => setIsHovered(null)}
              onClick={() => navigate('/atacama')}
            >
              <div className="destinations-map__destination-label">
                <p>{t('pages/home:map.destinations.atacama')}</p>
              </div>
              <div className="destinations-map__line-connector">
                <img src={images.mapIcons.atacamaLineMarker} alt="" />
              </div>
            </div>
            
            <div 
              className={`destinations-map__destination destinations-map__destination--patagonia ${isHovered === 'patagonia' ? 'destinations-map__destination--hover' : ''}`}
              onMouseEnter={() => setIsHovered('patagonia')}
              onMouseLeave={() => setIsHovered(null)}
              onClick={() => navigate('/patagonia')}
            >
              <div className="destinations-map__destination-label">
                <p>{t('pages/home:map.destinations.patagonia')}</p>
              </div>
              <div className="destinations-map__line-connector">
                <img src={images.mapIcons.atacamaLineMarker} alt="" />
              </div>
            </div>
            
            <div 
              className={`destinations-map__destination destinations-map__destination--santa-catarina ${isHovered === 'santa-catarina' ? 'destinations-map__destination--hover' : ''}`}
              onMouseEnter={() => setIsHovered('santa-catarina')}
              onMouseLeave={() => setIsHovered(null)}
              onClick={() => navigate('/santa-catarina')}
            >
              <div className="destinations-map__line-connector destinations-map__line-connector--reversed">
                <img src={images.mapIcons.santaCatarinaLineMarker} alt="" />
              </div>
              <div className="destinations-map__destination-label">
                <p>{t('pages/home:map.destinations.santaCatarina')}</p>
              </div>
            </div>
            
            <div 
              className={`destinations-map__destination destinations-map__destination--iguazu ${isHovered === 'iguazu' ? 'destinations-map__destination--hover' : ''}`}
              onMouseEnter={() => setIsHovered('iguazu')}
              onMouseLeave={() => setIsHovered(null)}
              onClick={() => navigate('/iguazu')}
            >
              <div className="destinations-map__location-pointer">
                <img src={images.mapIcons.locationPointerFigma} alt="" />
              </div>
              <div className="destinations-map__destination-label destinations-map__destination-label--iguazu">
                <p>{t('pages/home:map.destinations.iguazu')}</p>
              </div>
            </div>
            
            <div 
              className={`destinations-map__destination destinations-map__destination--mendoza ${isHovered === 'mendoza' ? 'destinations-map__destination--hover' : ''}`}
              onMouseEnter={() => setIsHovered('mendoza')}
              onMouseLeave={() => setIsHovered(null)}
              onClick={() => navigate('/mendoza')}
            >
              <div className="destinations-map__destination-label">
                <p>{t('pages/home:map.destinations.mendoza')}</p>
              </div>
              <div className="destinations-map__line-connector">
                <img src={images.mapIcons.atacamaLineMarker} alt="" />
              </div>
            </div>

            {/* City Labels */}
            <div className="destinations-map__city destinations-map__city--foz">
              <p>{t('pages/home:map.cities.foz')}</p>
            </div>
            
            <div className="destinations-map__waypoint destinations-map__waypoint--punta-arenas">
              <div className="destinations-map__waypoint-icon">
                <img src={images.mapIcons.waypoint} alt="" />
              </div>
              <p>{t('pages/home:map.cities.puntaArenas')}</p>
            </div>
            
            <div className="destinations-map__waypoint destinations-map__waypoint--el-calafate">
              <div className="destinations-map__waypoint-icon">
                <img src={images.mapIcons.waypoint} alt="" />
              </div>
              <p>{t('pages/home:map.cities.elCalafate')}</p>
            </div>
            
            <div className="destinations-map__waypoint destinations-map__waypoint--rio">
              <div className="destinations-map__waypoint-icon">
                <img src={images.mapIcons.waypoint} alt="" />
              </div>
              <p>{t('pages/home:map.cities.rio')}</p>
            </div>
          </div>

          {/* Flight Arrows - only show if flights toggle is on */}
          <div className={`destinations-map__arrows ${showFlights ? 'destinations-map__arrows--visible' : ''}`}>
            <img src="/assets/figma-map/734a9a38060f02f3960d5e24a34c844d5442796c.svg" alt={t('pages/home:map.altTexts.flightConnections')} />
          </div>

          {/* Airports - only show if airports toggle is on */}
          <div className={`destinations-map__airports ${showAirports ? 'destinations-map__airports--visible' : ''}`}>
              <div className="destinations-map__airport destinations-map__airport--santiago">
                <p>{t('pages/home:map.cities.santiago')}</p>
                <div className="destinations-map__waypoint-icon">
                  <img src={images.mapIcons.waypoint} alt="" />
                </div>
              </div>
              
              <div className="destinations-map__airport destinations-map__airport--buenos-aires">
                <div className="destinations-map__waypoint-icon">
                  <img src={images.mapIcons.waypoint} alt="" />
                </div>
                <p>{t('pages/home:map.cities.buenosAires')}</p>
              </div>
              
              <div className="destinations-map__airport destinations-map__airport--san-paolo">
                <div className="destinations-map__waypoint-icon">
                  <img src={images.mapIcons.waypoint} alt="" />
                </div>
                <p>{t('pages/home:map.cities.sanPaolo')}</p>
              </div>
              
              <div className="destinations-map__airport destinations-map__airport--puerto-natales">
                <p>{t('pages/home:map.cities.puertoNatales')}</p>
                <div className="destinations-map__waypoint-icon">
                  <img src={images.mapIcons.waypointAlt} alt="" />
                </div>
              </div>
              
              <div className="destinations-map__airport destinations-map__airport--calama">
                <p>{t('pages/home:map.cities.calama')}</p>
                <div className="destinations-map__waypoint-icon">
                  <img src={images.mapIcons.waypoint} alt="" />
                </div>
              </div>
              
              <div className="destinations-map__airport destinations-map__airport--puerto-iguazu">
                <p>{t('pages/home:map.cities.puertoIguazu')}</p>
                <div className="destinations-map__waypoint-icon">
                  <img src={images.mapIcons.waypoint} alt="" />
                </div>
              </div>
              
              <div className="destinations-map__airport destinations-map__airport--punta-arenas">
                <div className="destinations-map__waypoint-icon">
                  <img src={images.mapIcons.waypoint} alt="" />
                </div>
                <p>{t('pages/home:map.cities.puntaArenas')}</p>
              </div>
              
              <div className="destinations-map__airport destinations-map__airport--el-calafate">
                <div className="destinations-map__waypoint-icon">
                  <img src={images.mapIcons.waypoint} alt="" />
                </div>
                <p>{t('pages/home:map.cities.elCalafate')}</p>
              </div>
              
              <div className="destinations-map__airport destinations-map__airport--rio">
                <div className="destinations-map__waypoint-icon">
                  <img src={images.mapIcons.waypoint} alt="" />
                </div>
                <p>{t('pages/home:map.cities.rio')}</p>
              </div>
              
              <div className="destinations-map__airport destinations-map__airport--foz">
                <p>{t('pages/home:map.cities.foz')}</p>
                <div className="destinations-map__waypoint-icon">
                  <img src={images.mapIcons.waypoint} alt="" />
                </div>
              </div>
            </div>
        </div>
      </div>
    </section>
  );
};